import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, notification, Row, Table, Tooltip } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { getPagination, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { isEmpty, orderBy } from 'lodash'
import { withProfile } from '../../modules'
import {
  Can,
  Icon,
  IconContainer,
  PageContainer,
  PageTitleRow,
} from '../../components'
import { handleBackendError } from '../../utils'
import CountryDetailsModal from './CountryDetailsModal'

import { getCountryList, updateCountry } from './api'

const CountryManagement = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [countries, setCountries] = useState([])
  const [selectedCountry, setSelectedCountry] = useState()
  const [isLoading, setIsLoading] = useState(false)

  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString())) {
      setSearchParams({ items_per_page: 25, page: 1 })
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString()))
      fetchCountryList(searchParams.toString())
  }, [searchParams])

  const fetchCountryList = (params) => {
    setIsLoading(true)

    getCountryList(params)
      .then(({ data }) => {
        const sortedCountries = orderBy(
          data.data,
          ['status', 'name'],
          ['asc', 'asc'],
        )
        setCountries(sortedCountries)
        setMetaData(data.meta)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)

        handleFailure(error, 'Fetching countries failed!')
      })
  }

  const isSuperAdmin = user?.me?.roles?.includes('super_admin')

  const tableColumns = [
    isSuperAdmin && {
      className: 'countryStatusSelenium',
      dataIndex: 'status',
      key: 'status',
      title: 'Status',
    },
    {
      className: 'countryNameSelenium',
      dataIndex: 'code',
      key: 'name',
      title: 'Country name',
    },
    {
      className: 'numberOfReturnedPackagesSelenium',
      dataIndex: 'number_of_returned_items',
      key: 'number_of_returned_items',
      title: 'Number of returned packages',
    },
    {
      className: 'privacyPolicyUrlSelenium',
      dataIndex: 'privacy_policy_url',
      key: 'privacyPolicyUrl',
      render: (_, record) => (
        <a href={record.privacy_policy_url} rel="noopener noreferrer">
          {record.privacy_policy_url}
        </a>
      ),
      title: 'Link to terms and conditions',
    },
    {
      className: 'countryActionSelenium',
      key: 'action',
      render: (_, record) => (
        <Can
          no={() => (
            <IconContainer>
              <Tooltip title="View">
                <Icon
                  data-testid="viewBtn"
                  name="updateCountrySelenium"
                  onClick={() => handleOpenModal(record)}
                  type="eye"
                />
              </Tooltip>
            </IconContainer>
          )}
          requiredPermission="country.put.country_id"
          yes={() => (
            <IconContainer>
              <Tooltip title="Edit">
                <Icon
                  data-testid="editBtn"
                  name="updateCountrySelenium"
                  onClick={() => handleOpenModal(record)}
                  type="edit"
                />
              </Tooltip>
            </IconContainer>
          )}
        />
      ),
      title: 'Actions',
    },
  ].filter(Boolean)

  const handleFormSubmit = (formData) => {
    updateCountry(formData)
      .then(() =>
        handleSuccess({ description: 'Country updated' }),
      )
      .catch((error) =>
        handleFailure(error, { description: 'Country update failed' }),
      )
  }

  const handleOpenModal = (selectedCountry) => {
    setSelectedCountry(selectedCountry)
    setErrorMessage(null)
    setModalVisibility(true)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions) => {
    fetchCountryList(searchParams.toString())
    setErrorMessage(null)
    setModalVisibility(false)

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)
    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  return (
    <PageContainer>
      <PageTitleRow title="Countries" />
      <Row>
        <Col span={24}>
          <Table
            className="countryManagementPageTableSelenium"
            columns={tableColumns}
            data-testid="countries"
            dataSource={countries}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey="code"
          />

          {isModalVisible && (
            <CountryDetailsModal
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onSubmit={handleFormSubmit}
              selectedCountry={selectedCountry}
              setModalLoading={setModalLoading}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

CountryManagement.propTypes = {
  user: PropTypes.array.isRequired,
}

export default withProfile(CountryManagement)
